import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, ArrowLeft, Phone } from 'lucide-react';

import { SITE_URL } from './GlobalSchema';
const SITE = SITE_URL;

const buildSchemas = (service) => ({
  breadcrumbs: {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE}/services` },
      { '@type': 'ListItem', position: 3, name: service.title, item: `${SITE}/services/${service.slug}` }
    ]
  },
  service: {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.hero,
    provider: { '@id': `${SITE}/#organization` },
    serviceType: service.serviceType,
    areaServed: 'Worldwide',
    url: `${SITE}/services/${service.slug}`
  },
  faq: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: service.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  }
});

const ServicePageHead = ({ service, schemas }) => (
  <Helmet>
    <title>{service.metaTitle}</title>
    <meta name="description" content={service.metaDesc} />
    <link rel="canonical" href={`${SITE}/services/${service.slug}`} />
    <meta property="og:title" content={service.metaTitle} />
    <meta property="og:description" content={service.metaDesc} />
    <meta property="og:url" content={`${SITE}/services/${service.slug}`} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="ABL Finance" />
    <meta property="og:image" content={`${SITE}/logo.png`} />
    <meta property="og:locale" content="en_GB" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={service.metaTitle} />
    <meta name="twitter:description" content={service.metaDesc} />
    <meta name="twitter:image" content={`${SITE}/logo.png`} />
    <script type="application/ld+json">{JSON.stringify(schemas.breadcrumbs)}</script>
    <script type="application/ld+json">{JSON.stringify(schemas.service)}</script>
    <script type="application/ld+json">{JSON.stringify(schemas.faq)}</script>
  </Helmet>
);

const ServicePageHero = ({ service }) => (
  <section className="relative py-20 bg-gradient-to-r from-slate-900 to-emerald-900 text-white">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <Link
        to="/services"
        className="inline-flex items-center text-emerald-300 hover:text-emerald-200 mb-4 text-sm"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> All Services
      </Link>
      <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="service-h1">
        {service.title}
      </h1>
      <p className="text-lg md:text-xl text-gray-200 leading-relaxed">{service.hero}</p>
    </div>
  </section>
);

const ServiceSectionBody = ({ section }) => (
  <section>
    <h2 className="text-2xl font-bold text-slate-900 mb-4">{section.heading}</h2>
    {section.paragraphs.map((p) => (
      <p key={p.slice(0, 60)} className="text-slate-600 leading-relaxed mb-4">
        {p}
      </p>
    ))}
    {section.bullets && (
      <ul className="list-disc list-inside space-y-2 text-slate-600 mt-2">
        {section.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    )}
  </section>
);

const ServicePageFAQ = ({ faqs }) => (
  <section>
    <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
    <div className="space-y-4">
      {faqs.map((faq) => (
        <Card key={faq.q}>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
            <p className="text-slate-600 leading-relaxed">{faq.a}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </section>
);

const ServicePageRelated = ({ related }) => {
  if (!related || related.length === 0) return null;
  return (
    <section>
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Related Services</h2>
      <div className="flex flex-wrap gap-3">
        {related.map((r) => (
          <Link key={r.slug} to={`/services/${r.slug}`}>
            <Button
              variant="outline"
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            >
              {r.name}
            </Button>
          </Link>
        ))}
      </div>
    </section>
  );
};

const ServicePageCTA = ({ title }) => (
  <section className="py-16 bg-emerald-600 text-white text-center">
    <div className="max-w-3xl mx-auto px-4">
      <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
      <p className="text-lg text-emerald-100 mb-8">
        Contact our team for a free consultation on {title.toLowerCase()} solutions.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button asChild size="lg" className="self-center sm:self-auto bg-white text-emerald-700 hover:bg-gray-100">
          <Link to="/contact">
            Request a Consultation <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="self-center sm:self-auto border-white text-white hover:bg-white hover:text-emerald-700"
        >
          <a href="tel:+442032914022">
            <Phone className="mr-2 w-4 h-4" /> +44 203 291 4022
          </a>
        </Button>
      </div>
    </div>
  </section>
);

const ServicePage = ({ service }) => {
  const schemas = buildSchemas(service);
  return (
    <div className="bg-white">
      <ServicePageHead service={service} schemas={schemas} />
      <ServicePageHero service={service} />
      <div className="max-w-4xl mx-auto px-4 py-16 space-y-12">
        {service.sections.map((section) => (
          <ServiceSectionBody key={section.heading} section={section} />
        ))}
        <ServicePageFAQ faqs={service.faqs} />
        <ServicePageRelated related={service.related} />
      </div>
      <ServicePageCTA title={service.title} />
      <div className="max-w-4xl mx-auto px-4 py-4">
        <p className="text-sm text-slate-500 text-right">Last updated: May 2026</p>
      </div>
    </div>
  );
};

export default ServicePage;
